import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNav } from '../../store/nav';
import { haptic } from '../../hooks/useTelegram';
import { useBackHandler } from '../../hooks/useBackHandler';
import { useActiveWallet } from '../../store/wallets';
import { useBalances } from '../../hooks/useChain';
import { useSession } from '../../hooks/useSession';
import { useToast } from '../../store/toast';
import { feeQuoteApi } from '../../lib/transfers';
import {
  useOfframpChannels,
  useOfframpKyc,
  useSellCrypto,
  usePayoutStatus,
} from '../../hooks/useOfframp';
import {
  offrampQuoteApi,
  offrampStartPhoneApi,
  offrampVerifyPhoneApi,
  offrampIdentifyApi,
  offrampKycApi,
  isFinalPayout,
  type OfframpChannel,
  type OfframpQuote,
  type Payout,
} from '../../lib/offramp';
import { FigIcon } from '../FigIcon';
import { ReauthGate } from './ReauthGate';

type Step = 'amount' | 'kyc' | 'recipient' | 'confirm' | 'auth' | 'working' | 'done' | 'failed';

/** Dev/browser has no chain balance. Same stand-in the send screen uses. */
const DEV_USDT_BALANCE = 1_000_000;

function fmtUsdt(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const rounded = Math.round(n * 1e6) / 1e6;
  return rounded.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

/** Never rounds up, so Max cannot land 0.000001 above the spendable balance. */
function floorUsdt(n: number): string {
  if (!(n > 0)) return '0';
  const floored = Math.floor((n + 1e-9) * 1e6) / 1e6;
  return floored.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

function fiatLocale(language: string): string {
  return language.toLowerCase().startsWith('ru') ? 'ru-RU' : 'en-US';
}

function fmtFiat(n: number, language: string): string {
  return n.toLocaleString(fiatLocale(language), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Cashing USDT out to local money.
 *
 * Unlike every other flow here, the destination is typed in rather than chosen
 * from something the app already knows — so the recipient form is driven by the
 * channel the provider offers, and when the operator has pinned payouts to the
 * account holder the name comes from verification and cannot be edited.
 */
export function SellScreen() {
  const { t, i18n } = useTranslation();
  const pop = useNav((s) => s.pop);
  const showToast = useToast((s) => s.show);
  const { token } = useSession();
  const wallet = useActiveWallet();
  const balancesQ = useBalances(wallet?.address);
  const [hold, setHold] = useState<{ charge: number; reserve: number } | null>(null);
  const [feeStatus, setFeeStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const { sell } = useSellCrypto();

  const channelsQ = useOfframpChannels();
  const kycQ = useOfframpKyc();

  const [step, setStep] = useState<Step>('amount');
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState<OfframpChannel | null>(null);
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<OfframpQuote | null>(null);
  const [recipient, setRecipient] = useState<Record<string, string>>({});
  const [payout, setPayout] = useState<Payout | null>(null);

  const channels = useMemo(() => {
    const list = channelsQ.data ?? [];
    return [...list].sort((a, b) => {
      const rank = (c: OfframpChannel) => (c.currency.toUpperCase() === 'RUB' ? 0 : 1);
      return rank(a) - rank(b);
    });
  }, [channelsQ.data]);
  const profile = kycQ.data ?? null;

  // Pick a channel once. Prefer KZT when several are open; never keep a
  // selection the provider has since dropped.
  useEffect(() => {
    if (!channels.length) {
      if (channel) setChannel(null);
      return;
    }
    const stillOpen =
      !!channel &&
      channels.some((c) => c.type === channel.type && c.currency === channel.currency);
    if (stillOpen) return;

    if (channels.length === 1) {
      setChannel(channels[0]!);
      return;
    }
    const rub =
      channels.find((c) => c.currency.toUpperCase() === 'RUB' && c.type === 'sbp') ??
      channels.find((c) => c.currency.toUpperCase() === 'RUB');
    if (rub) setChannel(rub);
  }, [channels, channel]);

  const settling = step === 'working' && payout && !isFinalPayout(payout.status) ? payout.id : null;
  const { data: live } = usePayoutStatus(settling);
  useEffect(() => {
    if (!live || step !== 'working') return;
    if (!isFinalPayout(live.status)) return;
    setPayout(live);
    haptic('heavy');
    setStep(live.status === 'completed' ? 'done' : 'failed');
  }, [live, step]);

  useBackHandler(step === 'kyc' || step === 'recipient', () => setStep('amount'));
  useBackHandler(step === 'confirm', () => setStep('recipient'));
  useBackHandler(step === 'done' || step === 'failed', () => pop());
  useBackHandler(step === 'working' && !!payout, () => pop());

  const balanceKnown = !token || balancesQ.data != null;
  const balance = token ? (balancesQ.data?.usdt ?? 0) : DEV_USDT_BALANCE;
  // A real session waits for the quote. Guessing a flat $2 let Max exceed what the send accepts.
  const feeKnown = !token || feeStatus === 'ready';
  const feeReserve = !token ? 0 : (hold?.reserve ?? 0);
  const chargeFee = !token ? 0 : (hold?.charge ?? 0);
  const spendable = balanceKnown && feeKnown ? Math.max(0, balance - feeReserve) : 0;
  const short =
    quote != null && balanceKnown && feeKnown && balance + 1e-8 < quote.totalUsdt + feeReserve;

  useEffect(() => {
    const to = channel?.payTo;
    const from = wallet?.address;
    if (!token || !to || !from) {
      setHold(null);
      setFeeStatus(token && channel && !to ? 'error' : 'idle');
      return;
    }
    // Same planner as create — pass USDT amount when we have an offramp quote.
    const sendAmount =
      quote != null && quote.totalUsdt > 0 ? String(quote.totalUsdt) : undefined;
    let alive = true;
    setFeeStatus('loading');
    setHold(null);
    feeQuoteApi('USDT', to, from, sendAmount)
      .then((r) => {
        if (!alive) return;
        const charge = r.chargeUsd ?? r.feeUsd;
        const reserve = r.reserveUsd ?? charge;
        setHold({ charge, reserve });
        setFeeStatus('ready');
      })
      .catch(() => {
        if (!alive) return;
        setHold(null);
        setFeeStatus('error');
      });
    return () => {
      alive = false;
    };
  }, [token, channel, channel?.payTo, wallet?.address, quote?.totalUsdt]);

  const mapError = (e: unknown): string => {
    const code = e instanceof Error ? e.message : String(e);
    const map: Record<string, string> = {
      offramp_unavailable: t('sell.err.unavailable'),
      offramp_paused: t('sell.err.unavailable'),
      channel_unavailable: t('sell.err.unavailable'),
      invalid_amount: t('sell.err.invalidAmount'),
      amount_out_of_range: t('sell.err.outOfRange'),
      above_per_tx_limit: t('sell.err.perTxLimit'),
      above_daily_limit: t('sell.err.dailyLimit'),
      kyc_required: t('sell.err.kycRequired'),
      kyc_rejected: t('sell.err.kycRejected'),
      recipient_must_be_self: t('sell.err.selfOnly'),
      invalid_recipient: t('sell.err.invalidRecipient'),
      invalid_phone: t('sell.err.invalidPhone'),
      invalid_otp: t('sell.err.invalidOtp'),
      invalid_passport: t('sell.err.invalidPassport'),
      rate_limited: t('sell.err.rateLimited'),
      rate_moved: t('sell.err.rateMoved'),
      kyc_pending: t('sell.err.kycPending'),
      insufficient_balance: t('sell.err.insufficient'),
      offramp_float_short: t('sell.err.floatShort'),
      payment_not_confirmed: t('sell.err.notConfirmed'),
      transfer_in_progress: t('send.err.inProgress'),
      transfer_preparing: t('send.err.preparing'),
      rpc_unavailable: t('send.err.network'),
      timeout: t('send.err.timeout'),
      ws_timeout: t('send.err.timeout'),
      ws_closed: t('send.err.timeout'),
      signed_transaction_expired: t('send.err.signedExpired'),
    };
    return map[code] ?? t('sell.err.generic');
  };

  const priceIt = async () => {
    if (!channel || !balanceKnown || !feeKnown) return;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return;
    setBusy(true);
    try {
      const q = await offrampQuoteApi(channel.type, channel.currency, amount);
      setQuote(q);
      // Kazakhstan (and any rail the provider settles without a customer) asks
      // for the sender on the payout itself. Russian KYC would only block it.
      setStep(channel.needsCustomer === false || profile?.ready ? 'recipient' : 'kyc');
    } catch (e) {
      haptic();
      showToast(mapError(e));
    } finally {
      setBusy(false);
    }
  };

  // Reauth cleared → send the USDT and open the payout. Returns true so the gate
  // never blames the PIN for something that failed after it was accepted.
  const runSell = async (reauthToken?: string): Promise<boolean> => {
    if (!quote || !wallet) return true;
    setStep('working');
    try {
      const result = await sell({
        quote,
        recipient,
        walletId: wallet.id,
        index: wallet.derivationIndex ?? 0,
        address: wallet.address,
        reauthToken,
      });
      setPayout(result);
      if (isFinalPayout(result.status)) {
        haptic('heavy');
        setStep(result.status === 'completed' ? 'done' : 'failed');
      }
    } catch (e) {
      haptic();
      console.error('[sell] failed', e);
      const moved = e as Error & { quote?: OfframpQuote; paid?: boolean };
      if (moved.message === 'rate_moved' && moved.quote) {
        setQuote(moved.quote);
        showToast(t('sell.err.rateMoved'));
        setStep('confirm');
        return true;
      }
      showToast(mapError(e));
      // Chain has not confirmed yet. The transfer is kept, so another tap
      // opens the payout instead of charging again — and nothing is refunded.
      if (moved.message === 'payment_not_confirmed') {
        setStep('confirm');
        return true;
      }
      if (moved.paid && quote) {
        setPayout({
          id: '',
          externalId: '',
          type: quote.type,
          country: null,
          fiatCurrency: quote.currency,
          fiatAmount: quote.fiatAmount,
          amountUsdt: quote.amountUsdt,
          serviceFeeUsdt: quote.serviceFeeUsdt,
          totalUsdt: quote.totalUsdt,
          rate: quote.rate,
          recipientMasked: null,
          status: 'failed',
          statusReason: 'rate_moved',
          refundRequired: true,
          reference: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setStep('failed');
      } else {
        setStep('confirm');
      }
    }
    return true;
  };

  // ── Nothing on offer ─────────────────────────────────────────────────────
  if (!channelsQ.isLoading && channels.length === 0) {
    return (
      <Frame title={t('sell.title')}>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
          <span className="grid size-[56px] place-items-center rounded-[18px] bg-card shadow-card">
            <FigIcon name="pfReceipt" size={30} className="text-accent" />
          </span>
          <p className="max-w-[280px] text-[15px] leading-[1.44] text-muted">
            {t('sell.unavailable')}
          </p>
        </div>
      </Frame>
    );
  }

  // ── Settled ──────────────────────────────────────────────────────────────
  if (step === 'done' && payout) {
    return (
      <Frame title={t('sell.title')}>
        <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
          <span className="grid size-[72px] place-items-center rounded-full bg-accent text-white">
            <FigIcon name="pfCheck" size={34} />
          </span>
          <h1 className="text-[24px] font-medium text-ink">{t('sell.doneTitle')}</h1>
          <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">
            {t('sell.doneDesc', {
              fiat: `${fmtFiat(payout.fiatAmount, i18n.language)} ${payout.fiatCurrency}`,
              recipient: payout.recipientMasked ?? '',
            })}
          </p>
        </div>
        <Cta onClick={pop}>{t('sell.done')}</Cta>
      </Frame>
    );
  }

  // ── Failed after we took the money ───────────────────────────────────────
  if (step === 'failed' && payout) {
    return (
      <Frame title={t('sell.title')}>
        <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
          <span className="grid size-[72px] place-items-center rounded-full bg-negative text-white">
            <FigIcon name="pfReceipt" size={34} />
          </span>
          <h1 className="text-[24px] font-medium text-ink">{t('sell.failedTitle')}</h1>
          <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">
            {t('sell.failedDesc')}
          </p>
          <p className="max-w-[300px] text-[14px] leading-[1.44] text-muted">
            {t('sell.refund', { total: `${fmtUsdt(payout.totalUsdt + chargeFee)} USDT` })}
          </p>
        </div>
        <Cta onClick={pop}>{t('sell.done')}</Cta>
      </Frame>
    );
  }

  // ── Sending / settling ───────────────────────────────────────────────────
  if (step === 'working') {
    return createPortal(
      <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-5 bg-black/[0.32] px-10 backdrop-blur-[8px]">
        <span className="size-12 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
        <p className="text-[16px] font-medium text-white">{t('sell.working')}</p>
        <p className="max-w-[280px] text-center text-[13px] leading-[1.44] text-white/70">
          {payout ? t('sell.workingHint') : t('sell.sending')}
        </p>
        {payout && (
          <button type="button" onClick={pop} className="text-[14px] font-medium text-white/80">
            {t('sell.leave')}
          </button>
        )}
      </div>,
      document.body,
    );
  }

  if (step === 'auth') {
    return <ReauthGate onAuthed={runSell} bioTitle={t('sell.confirmTitle')} bioHint={t('security.sendUnlockHint')} />;
  }

  // ── Verification ─────────────────────────────────────────────────────────
  if (step === 'kyc') {
    return (
      <KycForm
        busy={busy}
        phoneSet={profile?.phoneSet === true}
        onBusy={setBusy}
        onError={(e) => {
          haptic();
          showToast(mapError(e));
        }}
        onReady={async () => {
          await kycQ.refetch();
          setStep('recipient');
        }}
      />
    );
  }

  // ── Recipient ────────────────────────────────────────────────────────────
  if (step === 'recipient' && channel && quote) {
    const missing = channel.fields.some((f) => f.required && !(recipient[f.name] ?? '').trim());
    return (
      <Frame title={t('sell.recipientTitle')}>
        <div className="flex flex-1 flex-col gap-3 pt-2">
          {channel.fields.map((f) => {
            // While payouts are pinned to the account holder the name is the
            // verified one, shown but not editable.
            const pinned =
              !!profile?.verifiedName &&
              (f.name === 'recipientName' || f.name === 'recipientFirstName');
            const value = pinned ? profile!.verifiedName : (recipient[f.name] ?? '');
            return (
              <label key={f.name} className="flex flex-col gap-1">
                <span className="text-[13px] text-muted">
                  {t(`sell.field.${f.name}`, { defaultValue: f.name })}
                  {!f.required && ` · ${t('sell.optional')}`}
                </span>
                <input
                  value={value}
                  readOnly={pinned}
                  inputMode={f.kind === 'pan' || f.kind === 'phone' || f.kind === 'bank' ? 'numeric' : 'text'}
                  onChange={(e) => setRecipient((r) => ({ ...r, [f.name]: e.target.value }))}
                  className={`h-[52px] rounded-[16px] bg-card px-4 text-[15px] text-ink outline-none ${
                    pinned ? 'opacity-70' : ''
                  }`}
                />
              </label>
            );
          })}
          {profile?.verifiedName && (
            <p className="mt-1 text-[13px] leading-[1.4] text-muted">
              {t('sell.selfOnlyHint', { name: profile.verifiedName })}
            </p>
          )}
        </div>
        <Cta
          disabled={missing}
          onClick={() => {
            // Pinned fields are not typed in, so carry them before confirming.
            const filled = { ...recipient };
            for (const f of channel.fields) {
              if (
                profile?.verifiedName &&
                (f.name === 'recipientName' || f.name === 'recipientFirstName')
              ) {
                filled[f.name] = profile.verifiedName;
              }
            }
            if (!recipientOk(channel, filled)) {
              haptic();
              showToast(t('sell.err.invalidRecipient'));
              return;
            }
            setRecipient(filled);
            setStep('confirm');
          }}
        >
          {t('sell.continue')}
        </Cta>
      </Frame>
    );
  }

  // ── Confirm ──────────────────────────────────────────────────────────────
  if (step === 'confirm' && quote && channel) {
    const debit = quote.totalUsdt + chargeFee;
    const debitLabel = `${fmtUsdt(debit)} USDT`;
    return (
      <Frame title={t('sell.confirmTitle')}>
        <div className="flex flex-1 flex-col justify-center">
          <div className="flex flex-col items-center gap-3">
            <span className="grid size-[56px] place-items-center rounded-[18px] bg-card shadow-card">
              <FigIcon name="pfReceipt" size={30} className="text-accent" />
            </span>
            <p className="text-[17px] font-semibold text-ink">{channel.label}</p>
            <p className="mt-2 text-[14px] text-muted">{t('sell.receiveLabel')}</p>
            <p className="[font-family:'SF_Pro_Rounded',system-ui] text-[32px] font-semibold text-ink">
              {fmtFiat(quote.fiatAmount, i18n.language)} {quote.currency}
            </p>
            <p className="-mt-1 text-[14px] text-muted">
              {t('sell.forUsdt', { usdt: fmtUsdt(quote.totalUsdt) })}
            </p>
          </div>

          <div className="mt-8 flex flex-col rounded-[24px] bg-card px-4">
            <Row label={t('sell.recipientTitle')} value={maskedRecipient(channel, recipient)} />
            <Row label={t('sell.amount')} value={`${fmtUsdt(quote.totalUsdt)} USDT`} />
            <Row label={t('sell.rate')} value={`1 USDT ≈ ${quote.rate.toFixed(4)} ${quote.currency}`} />
            {quote.serviceFeeUsdt > 0 && (
              <Row label={t('sell.serviceFee')} value={`${fmtUsdt(quote.serviceFeeUsdt)} USDT`} />
            )}
            {chargeFee > 0 && <Row label={t('sell.transferFee')} value={`${fmtUsdt(chargeFee)} USDT`} />}
            <Row label={t('sell.total')} value={debitLabel} strong last />
          </div>

          {short && <p className="mt-4 text-center text-[13px] text-negative">{t('sell.err.insufficient')}</p>}
        </div>
        <Cta
          disabled={short || !wallet || !feeKnown}
          onClick={() => {
            haptic('medium');
            setStep('auth');
          }}
        >
          {t('sell.cta', { total: debitLabel })}
        </Cta>
      </Frame>
    );
  }

  // ── Amount + channel ─────────────────────────────────────────────────────
  const typed = Number(amount);
  const ready = Number.isFinite(typed) && typed > 0 && !!channel;
  const canPrice = ready && balanceKnown && feeKnown;
  const over = canPrice && typed > spendable + 1e-8;
  const previewFiat = channel && typed > 0 ? typed * channel.rate : 0;
  const setMax = () => {
    if (!canPrice) return;
    haptic();
    setAmount(floorUsdt(spendable));
  };
  return (
    <Frame title={t('sell.title')}>
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        {channels.length > 0 && (
          <div className="flex w-full flex-col items-center gap-2">
            <p className="text-[14px] text-muted">{t('sell.channelHint')}</p>
            <div className="flex w-full flex-wrap justify-center gap-2">
              {channels.map((c) => (
                <button
                  key={`${c.type}-${c.currency}`}
                  type="button"
                  disabled={channels.length === 1}
                  onClick={() => {
                    if (channels.length === 1) return;
                    haptic();
                    setChannel(c);
                  }}
                  className={`rounded-full px-4 py-2 text-[14px] ${
                    channel?.type === c.type && channel?.currency === c.currency
                      ? 'bg-accent text-white'
                      : 'bg-card text-ink'
                  }`}
                >
                  {t(`sell.rail.${c.type}`, { defaultValue: c.label })} · {c.currency}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-[14px] text-muted">{t('sell.amountHint')}</p>
        <div className="flex items-center justify-center gap-2">
          <input
            autoFocus
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
            placeholder="0"
            style={{ width: `${Math.max((amount || '0').length, 1)}ch` }}
            className="bg-transparent text-[36px] font-semibold text-ink outline-none placeholder:text-muted [font-family:'SF_Pro_Rounded',system-ui]"
          />
          <span className="text-[20px] font-medium text-muted">USDT</span>
        </div>
        {channel && (
          <div className="flex flex-col items-center gap-1">
            <p className="text-[14px] text-muted">{t('sell.receiveLabel')}</p>
            <p className="[font-family:'SF_Pro_Rounded',system-ui] text-[32px] font-semibold text-ink">
              ≈ {fmtFiat(typed > 0 ? previewFiat : 0, i18n.language)} {channel.currency}
            </p>
          </div>
        )}
        {feeStatus === 'error' ? (
          <p className="text-center text-[15px] font-medium text-negative">{t('sell.err.fee')}</p>
        ) : !balanceKnown || (!!token && feeStatus !== 'ready') ? (
          <p className="text-[15px] font-medium text-muted">{t('sell.wait')}</p>
        ) : (
          <button type="button" onClick={setMax} className="text-[15px] font-medium text-accent active:opacity-70">
            {t('sell.max')} {floorUsdt(spendable)} USDT
          </button>
        )}
        {over && (
          <p className="text-center text-[13px] text-negative">{t('sell.err.insufficient')}</p>
        )}
        {channel && (
          <p className="text-center text-[13px] text-muted">
            1 USDT ≈ {channel.rate.toFixed(4)} {channel.currency}
            {channel.minFiat != null && channel.maxFiat != null
              ? ` · ${t('sell.fiatRange', {
                  min: channel.minFiat.toLocaleString(fiatLocale(i18n.language)),
                  max: channel.maxFiat.toLocaleString(fiatLocale(i18n.language)),
                  currency: channel.currency,
                })}`
              : ''}
          </p>
        )}
      </div>
      <Cta disabled={!canPrice || busy || over} onClick={priceIt}>
        {busy ? t('sell.wait') : t('sell.continue')}
      </Cta>
    </Frame>
  );
}

// ── pieces ─────────────────────────────────────────────────────────────────

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[70vh] flex-col px-4 pb-6 pt-2">
      <div className="flex h-9 items-center justify-center">
        <h1 className="text-[16px] font-medium text-ink">{title}</h1>
      </div>
      {children}
    </div>
  );
}

function Cta({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="mt-6 flex h-[56px] w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white transition-opacity active:bg-accent-ink disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function Row({
  label,
  value,
  strong,
  last,
}: {
  label: string;
  value: string;
  strong?: boolean;
  last?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between gap-3 py-4 ${last ? '' : 'border-b border-hairline'}`}>
      <span className={`text-[15px] ${strong ? 'font-medium text-ink' : 'text-muted'}`}>{label}</span>
      <span className="text-[15px] font-medium text-ink">{value}</span>
    </div>
  );
}

/** Self-declared verification: what the user states, no document check. The
 *  Latin spelling is the name the payout is checked against. */
function KycForm({
  busy,
  phoneSet,
  onBusy,
  onError,
  onReady,
}: {
  busy: boolean;
  phoneSet: boolean;
  onBusy: (v: boolean) => void;
  onError: (e: unknown) => void;
  onReady: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'phone' | 'otp' | 'passport'>(phoneSet ? 'passport' : 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [v, setV] = useState({ firstName: '', lastName: '', patronymic: '', birthdate: '', passport: '' });
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV((s) => ({ ...s, [k]: e.target.value }));

  const run = async (fn: () => Promise<{ ready: boolean }>) => {
    onBusy(true);
    try {
      const profile = await fn();
      if (profile.ready) {
        await onReady();
        return;
      }
      if (phase === 'phone') {
        setPhase('otp');
        return;
      }
      if (phase === 'otp') {
        setPhase('passport');
        return;
      }
      for (let i = 0; i < 4; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const latest = await offrampKycApi();
        if (latest?.ready) {
          await onReady();
          return;
        }
      }
      throw new Error('kyc_pending');
    } catch (e) {
      onError(e);
    } finally {
      onBusy(false);
    }
  };

  if (phase === 'phone' || phase === 'otp') {
    return (
      <Frame title={t('sell.kycTitle')}>
        <p className="pb-2 pt-1 text-center text-[14px] leading-[1.4] text-muted">
          {phase === 'phone' ? t('sell.kycPhoneHint') : t('sell.kycOtpHint')}
        </p>
        <input
          value={phase === 'phone' ? phone : otp}
          inputMode={phase === 'otp' ? 'numeric' : 'tel'}
          onChange={(e) => (phase === 'phone' ? setPhone(e.target.value) : setOtp(e.target.value))}
          placeholder={phase === 'phone' ? '+7' : ''}
          className="mt-4 h-[52px] rounded-[16px] bg-card px-4 text-[15px] text-ink outline-none"
        />
        <Cta
          disabled={busy || (phase === 'phone' ? phone.trim().length < 8 : otp.trim().length < 4)}
          onClick={() =>
            void run(async () =>
              phase === 'phone' ? offrampStartPhoneApi(phone.trim()) : offrampVerifyPhoneApi(otp.trim()),
            )
          }
        >
          {busy ? t('sell.wait') : t('sell.continue')}
        </Cta>
      </Frame>
    );
  }

  const missing = !v.firstName.trim() || !v.lastName.trim() || !v.birthdate || !v.passport.trim();
  return (
    <Frame title={t('sell.kycTitle')}>
      <p className="pb-2 pt-1 text-center text-[14px] leading-[1.4] text-muted">{t('sell.kycHint')}</p>
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto">
        {(['lastName', 'firstName', 'patronymic', 'birthdate', 'passport'] as const).map((key) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="text-[13px] text-muted">
              {t(`sell.kyc.${key}`)}
              {key === 'patronymic' && ` · ${t('sell.optional')}`}
            </span>
            <input
              value={v[key]}
              type={key === 'birthdate' ? 'date' : 'text'}
              onChange={set(key)}
              className="h-[52px] rounded-[16px] bg-card px-4 text-[15px] text-ink outline-none"
            />
          </label>
        ))}
      </div>
      <Cta
        disabled={missing || busy}
        onClick={() =>
          void run(() =>
            offrampIdentifyApi({
              firstName: v.firstName.trim(),
              lastName: v.lastName.trim(),
              ...(v.patronymic.trim() ? { patronymic: v.patronymic.trim() } : {}),
              birthdate: v.birthdate,
              passport: v.passport.trim(),
            }),
          )
        }
      >
        {busy ? t('sell.wait') : t('sell.continue')}
      </Cta>
    </Frame>
  );
}

function digitsOf(value: string): string {
  return value.replace(/\D/g, '');
}

function recipientOk(channel: OfframpChannel, values: Record<string, string>): boolean {
  for (const field of channel.fields) {
    const raw = (values[field.name] ?? '').trim();
    if (!raw) {
      if (field.required) return false;
      continue;
    }
    const digits = digitsOf(raw);
    if (field.kind === 'pan' && (digits.length < 13 || digits.length > 19)) return false;
    if (field.kind === 'phone' && (digits.length < 10 || digits.length > 15)) return false;
    if (field.kind === 'bank' && !digits) return false;
  }
  return true;
}

function maskedRecipient(channel: OfframpChannel, values: Record<string, string>): string {
  const field = channel.fields.find((f) => f.kind === 'pan' || f.kind === 'phone');
  const digits = digitsOf(field ? (values[field.name] ?? '') : '');
  if (digits.length < 4) return '••••';
  return `•••• ${digits.slice(-4)}`;
}

export { Frame as SellFrame };
